import axios from "axios";
import { normalizeId } from "../components/utils/normalize";
import { hideChildModal, hideModal } from "./modal";

/*****************/
/* INITIAL STATE */
/*****************/
const initialState = {
  loading: false,
  error: "",
  data: [],
};

/*********/
/* TYPES */
/*********/
const FETCH_LABELS = "FETCH_LABELS";
const FETCH_LABELS_SUCCESS = "FETCH_LABELS_SUCCESS";
const FETCH_LABELS_ERROR = "FETCH_LABELS_ERROR";
const FETCH_NEXT_LABELS = "FETCH_NEXT_LABELS";
const FETCH_NEXT_LABELS_SUCCESS = "FETCH_NEXT_LABELS_SUCCESS";
const CREATE_LABEL = "CREATE_LABEL";
const CREATE_LABEL_SUCCESS = "CREATE_LABEL_SUCCESS";
const CREATE_LABEL_ERROR = "CREATE_LABEL_ERROR";
const UPDATE_LABEL = "UPDATE_LABEL";
const UPDATE_LABEL_SUCCESS = "UPDATE_LABEL_SUCCESS";
const UPDATE_LABEL_ERROR = "UPDATE_LABEL_ERROR";
const DELETE_LABEL = "DELETE_LABEL";
const DELETE_LABEL_SUCCESS = "DELETE_LABEL_SUCCESS";
const DELETE_LABEL_ERROR = "DELETE_LABEL_ERROR";

/*******************/
/* ACTION CREATORS */
/*******************/
export const getLabels = () => async (dispatch) => {
  dispatch(loadLabels());

  const response = await axios({
    method: "get",
    url: `${process.env.REACT_APP_API_URL}/labels?page=1`,
    headers: {
      "Content-Type": "application/ld+json; charset=utf-8",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  if (response.data["hydra:member"].length === 0 || response.data["hydra:member"] === undefined) {
    dispatch(setError("No labels found"));
  } else {
    dispatch(setLabels(response.data["hydra:member"]));
    if (
      response.data &&
      response.data["hydra:view"] &&
      response.data["hydra:view"] !== undefined &&
      response.data["hydra:view"]["hydra:next"] &&
      response.data["hydra:view"]["hydra:next"] !== undefined
    ) {
      dispatch(getNextLabels(response.data["hydra:view"]["hydra:next"]));
    }
  }
};

export const getNextLabels = (nextUrl) => async (dispatch, getState) => {
  dispatch(loadNextLabels());

  const response = await axios({
    method: "get",
    url: `${process.env.REACT_APP_URL}${nextUrl}`,
    headers: {
      "Content-Type": "application/ld+json; charset=utf-8",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  dispatch(setNextLabels(response.data["hydra:member"]));

  if (
    response.data &&
    response.data["hydra:view"] &&
    response.data["hydra:view"] !== undefined &&
    response.data["hydra:view"]["hydra:next"] &&
    response.data["hydra:view"]["hydra:next"] !== undefined
  ) {
    dispatch(getNextLabels(response.data["hydra:view"]["hydra:next"]));
  }
};

export const createLabel = (label, callback) => async (dispatch, getState) => {
  dispatch({ type: CREATE_LABEL });

  const {
    modal: modal
  } = getState();

  try {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/labels`,
      headers: {
        "Content-Type": "application/ld+json; charset=utf-8",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      data: label,
    });

    if (callback) {
      callback("labels", [response.data["@id"]]);
    }

    dispatch({
      type: CREATE_LABEL_SUCCESS,
      payload: response.data,
    });
    
    if (modal.childModal) {
      dispatch(hideChildModal());
    } else {
      dispatch(hideModal());
    }

  } catch (error) {
    dispatch({
      type: CREATE_LABEL_ERROR,
      payload: error.message || "Failed to create label",
    });
  }
};

export const updateLabel = (label) => async (dispatch, getState) => {
  dispatch({ type: UPDATE_LABEL });

  const {
    modal: modal
  } = getState();

  try {
    const response = await axios({
      method: "put",
      url: `${process.env.REACT_APP_API_URL}/labels/${normalizeId(label["@id"])}`,
      headers: {
        "Content-Type": "application/ld+json; charset=utf-8",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      data: label,
    });

    dispatch({
      type: UPDATE_LABEL_SUCCESS,
      payload: response.data,
    });
    
    if (modal.childModal) {
      dispatch(hideChildModal());
    } else {
      dispatch(hideModal());
    }
  } catch (error) {
    dispatch({
      type: UPDATE_LABEL_ERROR,
      payload: error.message || "Failed to update label",
    });
  }
};

export const deleteLabel = () => ({ type: DELETE_LABEL });

export const removeLabel = (label) => async (dispatch, getState) => {
  dispatch(deleteLabel());

  const {
    labels: { data },
    modal: modal
  } = getState();

  const response = await axios({
    method: "delete",
    url: `${process.env.REACT_APP_URL}${label["@id"] || `/labels/${label.id}`}`,
    headers: {
      "Content-Type": "application/ld+json; charset=utf-8",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  if (response.status === 204) {
    const updated = data.filter(
      (c) => c["@id"] !== (label["@id"] || `/labels/${label.id}`)
    );
    
    dispatch(setLabels(updated));
   
    if (modal.childModal) {
      dispatch(hideChildModal());
    } else {
      dispatch(hideModal());
    }
  } else {
    dispatch(setError("Something went wrong deleting label"));
  }
};


export const loadLabels = () => ({ type: FETCH_LABELS });

export const setLabels = (labels) => ({
  type: FETCH_LABELS_SUCCESS,
  payload: { labels },
});

export const loadNextLabels = (page) => ({
  type: FETCH_NEXT_LABELS,
  payload: page,
});
export const setNextLabels = (labels) => ({
  type: FETCH_NEXT_LABELS_SUCCESS,
  payload: labels,
});

export const setError = (msg) => ({
  type: FETCH_LABELS_ERROR,
  payload: msg,
});

/***********/
/* REDUCER */
/***********/
const labelsReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case FETCH_LABELS:
      return {
        ...state,
        loading: true,
        error: "",
      };
    case FETCH_LABELS_SUCCESS:
      return {
        ...state,
        loading: false,
        error: "",
        data: payload.labels,
      };

    case FETCH_LABELS_ERROR:
      return {
        ...state,
        loading: false,
        error: payload,
      };
    case FETCH_NEXT_LABELS:
      return {
        ...state,
        error: "",
      };
    case FETCH_NEXT_LABELS_SUCCESS:
      return {
        ...state,
        loading: false,
        data: [...state.data, ...payload],
      };
    case CREATE_LABEL:
    case DELETE_LABEL:
    case UPDATE_LABEL:
      return {
        ...state,
        loading: true,
        error: "",
      };

    case CREATE_LABEL_SUCCESS:
      return {
        ...state,
        loading: false,
        data: [payload, ...state.data],
      };

    case UPDATE_LABEL_SUCCESS:
      return {
      ...state,
      loading: false,
      data: state.data.map((l) =>
        l["@id"] === payload["@id"] ? payload : l
      ),
    };

    case CREATE_LABEL_ERROR:
    case UPDATE_LABEL_ERROR:
      return {
        ...state,
        loading: false,
        error: payload,
      };
    case DELETE_LABEL_SUCCESS:
    case DELETE_LABEL_ERROR:
      return {
        ...state,
        loading: false,
        error: payload,
      };
    default:
      return state;
  }
};

export default labelsReducer;
